import { REORDER_ASSIST_EVENT } from '../../../constants/analytics-events';
import { Segment } from '../../../services/analytics-services/segment';

/**
 * @typedef {import('../types').ForecastingDataItem} ForecastingDataItem
 * @typedef {import('../types').QuantityValuesMap} QuantityValuesMap
 */

/**
 * Event types for reorder assist tracking
 */
export const REORDER_ASSIST_EVENTS = {
  CSV_EXPORT: 'csv_export',
  CREATE_PURCHASE_ORDER: 'create_purchase_order',
  ABANDON: 'abandon',
};

/**
 * Track reorder assist events with standardized payload
 * @param {string} eventType - Event type from REORDER_ASSIST_EVENTS
 * @param {Array<ForecastingDataItem>} data - Array of forecasting data items
 * @param {QuantityValuesMap} quantityValues - Map of user-adjusted quantities by item ID
 * @param {string} warehouseId - Warehouse ID for the event
 */
export const trackReorderAssistEvent = (eventType, data, quantityValues, warehouseId) => {
  if (!data || !Array.isArray(data)) {
    return;
  }

  const skusAndQuantities = data
    .slice(0, 10) // Limit to first 10 items
    .map((item) => ({
      sku: item.sku,
      quantity: quantityValues[item.id] ?? item.quantity ?? item.suggestedReorder ?? 0,
    }))
    .filter((item) => item.sku);

  const payload = {
    action: eventType,
    item_count: data.length,
    skus_and_quantities: skusAndQuantities,
    warehouse_id: warehouseId,
    source: 'reorder-assist',
  };

  Segment.trackEvent(REORDER_ASSIST_EVENT, payload);
};

/**
 * Track CSV export event
 * @param {Array<ForecastingDataItem>} data - Array of forecasting data items
 * @param {QuantityValuesMap} quantityValues - Map of user-adjusted quantities by item ID
 * @param {string} warehouseId - Warehouse ID for the event
 */
export const trackCsvExport = (data, quantityValues, warehouseId) => {
  trackReorderAssistEvent(REORDER_ASSIST_EVENTS.CSV_EXPORT, data, quantityValues, warehouseId);
};

/**
 * Track create purchase order event
 * @param {Array<ForecastingDataItem>} data - Array of forecasting data items
 * @param {QuantityValuesMap} quantityValues - Map of user-adjusted quantities by item ID
 * @param {string} warehouseId - Warehouse ID for the event
 */
export const trackCreatePurchaseOrder = (data, quantityValues, warehouseId) => {
  trackReorderAssistEvent(REORDER_ASSIST_EVENTS.CREATE_PURCHASE_ORDER, data, quantityValues, warehouseId);
};

/**
 * Track abandon event when user closes drawer without taking action
 * @param {Array<ForecastingDataItem>} data - Array of forecasting data items
 * @param {QuantityValuesMap} quantityValues - Map of user-adjusted quantities by item ID
 * @param {string} warehouseId - Warehouse ID for the event
 */
export const trackAbandon = (data, quantityValues, warehouseId) => {
  trackReorderAssistEvent(REORDER_ASSIST_EVENTS.ABANDON, data, quantityValues, warehouseId);
};
